import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import 'app/components/commons/Experience/Availability/utils';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { usePack } from 'app/hooks/data/core/admin/packs/usePack';
import { useGetHotel, useHotels } from 'app/hooks/data/hotels/useHotel';
import { useCountries } from 'app/hooks/data/useCountries';
import type { OpeningHours } from 'app/typings/core/admin/packs/openingHours/openingHours';
import type {
  MaxBookingConfiguration,
  PackResponse,
} from 'app/typings/core/admin/packs/pack';
import { utcToZonedWallClock } from 'app/utils/dates';
import { dayjs, toDayjs } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';
import { useSearchParams } from 'app/utils/searchParams';

export type MaxBookingConfigurationForm =
  | MaxBookingConfiguration
  | { type: 'none' };

export type PackInitialFormType = Partial<
  Omit<PackResponse, 'id' | 'maxBookingConfiguration' | 'launchDate'>
> & {
  id?: number;
  hotelId?: number;
  isDaytime?: boolean;
  nameForHotel?: string;
  nameForUser?: string;
  openingHours?: OpeningHours;
  maxBookingConfiguration?: MaxBookingConfigurationForm;
  ownerId?: number;
  launchDate?: Dayjs | null;
  launchTime?: Dayjs | null;
};

export const usePackData = () => {
  const { isEditMode } = useContext(ViewEditModeContext);

  const { id: packIdParam } = useParams<{ id: string }>();
  const { getSearchParam } = useSearchParams();

  const hotelId = getSearchParam('hotelId');
  const isDaytime = getSearchParam('isDaytime');

  const packId = useMemo(
    () => (packIdParam ? parseInt(packIdParam) : undefined),
    [packIdParam]
  );

  const parsedHotelId = useMemo(() => {
    if (hotelId) {
      const parsed = parseInt(hotelId);

      return isNaN(parsed) ? undefined : parsed;
    }

    return undefined;
  }, [hotelId]);

  const isCreatePage = !packId;

  const { data: fetchedPack, isFetching } = usePack(packId);

  const { data: hotel } = useGetHotel(
    isCreatePage ? parsedHotelId : fetchedPack?.hotelId
  );
  const { data: hotels } = useHotels();
  const { data: countries } = useCountries();

  const hotelTimezone = useMemo(
    () =>
      countries?.find((country) => country.id === hotel?.countryId)?.timezone,
    [countries, hotel?.countryId]
  );

  const initialValues: PackInitialFormType = useMemo(() => {
    if (isCreatePage) {
      const parsedIsDaytime =
        isDaytime === 'true' ? true : isDaytime === 'false' ? false : undefined;

      return {
        hotelId: parsedHotelId,
        isDaytime: parsedIsDaytime,
        nameForHotel: undefined,
        nameForUser: undefined,
        maxBookingConfiguration: { type: 'none' },
        ownerId: parsedIsDaytime ? hotel?.managerId : undefined,
      };
    }

    const launchWallClock = hotelTimezone
      ? utcToZonedWallClock(fetchedPack?.launchDate, hotelTimezone)
      : undefined;

    return {
      ...fetchedPack,
      nameForHotel: fetchedPack?.nameForHotel || '',
      nameForUser: fetchedPack?.nameForUser || '',
      isDaytime: fetchedPack?.isDaytime ?? false,
      launchDate: toDayjs(launchWallClock),
      launchTime: toDayjs(launchWallClock),
      maxBookingConfiguration: fetchedPack?.maxBookingConfiguration ?? {
        type: 'none',
      },
      ownerId: fetchedPack?.ownerId,
      disclaimers: fetchedPack?.disclaimers
        ?.map((disclaimer) => ({
          ...disclaimer,
          dateRange: [
            toDayjs(disclaimer.startDate) ?? dayjs(),
            toDayjs(disclaimer.endDate),
          ],
        }))
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    };
  }, [
    isCreatePage,
    fetchedPack,
    parsedHotelId,
    isDaytime,
    hotel,
    hotelTimezone,
  ]);

  const isDataLoaded = useMemo(() => {
    if (isCreatePage) {
      return !!hotels && !!hotel && !!countries;
    }

    return Boolean(fetchedPack && hotel && hotels && countries);
  }, [isCreatePage, fetchedPack, hotel, hotels, countries]);

  const title = isCreatePage
    ? 'Create new package'
    : fetchedPack?.nameForHotel || 'Package Details';

  const buttonSaveText = isCreatePage
    ? 'Create new package'
    : isEditMode
    ? 'Save'
    : 'Edit';

  return {
    packId,
    isCreatePage,
    hotel,
    hotelTimezone,
    initialValues,
    isDataLoaded,
    buttonSaveText,
    title,
    fetchedPack,
    isFetching,
  };
};
